import { useEffect, useState, Fragment, useRef } from 'react';
import { userAtom } from '@/global-store';
import { Collapse } from '@eye-able/ui';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useAtomValue } from 'jotai';
import { useLocation } from 'react-router';
import { allowedToShowItem } from './allowedToShowItem';
import { getHiddenNavigationItems } from '@/helpers/navigationVisibility';
import NavItem from '@layout/Sidebar/MenuList/NavItem';
import { useSidebar } from '@layout/Sidebar/SidebarContext';
import SidebarNavButton from '@layout/Sidebar/MenuList/SidebarNavButton';
import NavItemLabel from '@layout/Sidebar/MenuList/NavItemLabel';
import { useNavigate } from 'react-router-dom';
import type { MenuItem } from '@layout/Sidebar/MenuList/types';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { hasMatchingNavigationChild } from './routeMatchesNavigation';

type NavCollapseProps = {
  item: MenuItem;
  level: number;
};

function NavCollapse({ item, level }: NavCollapseProps) {
  const { t } = useInlineTranslation();
  const user = useAtomValue(userAtom);
  const { isMobile } = useSidebar();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const defaultExpanded = item.handle.defaultExpanded || false;
  const [open, setOpen] = useState(defaultExpanded);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const hiddenNavigationItems = getHiddenNavigationItems(user);
  const menus = (item.children ?? []).filter(
    (child) =>
      !hiddenNavigationItems.includes(child.path) &&
      allowedToShowItem(child.handle.access, user, child.path),
  );

  const collapseId = `nav-collapse-${item.path.replaceAll('/', '-')}`;

  useEffect(() => {
    if (defaultExpanded) {
      setOpen(true);
      return;
    }

    setOpen(hasMatchingNavigationChild(item.children, pathname));
  }, [defaultExpanded, item.children, pathname]);

  const handleClick = () => {
    if (open) {
      setOpen(false);
    } else {
      setOpen(true);

      // For desktop, automatically navigate to the first visible subitem.
      // Uses the filtered menu (not raw children) so hidden routes — e.g. the
      // section's index redirect — are skipped regardless of their position.
      if (!isMobile && menus[0]?.path) {
        void navigate(menus[0].path);
      }
    }
  };

  const handleEscapeCollapse = () => {
    setOpen(false);

    buttonRef.current?.focus();
  };

  return (
    <Fragment>
      <SidebarNavButton
        component='button'
        level={level}
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={collapseId}
        onClick={handleClick}
        ref={buttonRef}
        tooltip={
          item.handle.i18nTooltipKey ? t(item.handle.i18nTooltipKey) : undefined
        }
      >
        <NavItemLabel item={item} />

        <div className='eye:ml-auto'>
          {open ? <IconChevronUp /> : <IconChevronDown />}
        </div>
      </SidebarNavButton>
      <Collapse id={collapseId} open={open} timeout={300} unmountOnExit>
        <ul>
          {menus.map((child) => (
            <li key={child.path}>
              <NavItem
                item={child}
                level={level + 1}
                onEscapeCollapse={handleEscapeCollapse}
              />
            </li>
          ))}
        </ul>
      </Collapse>
    </Fragment>
  );
}

export default NavCollapse;
